import * as z from "@-/com.npmjs.zod";

export const DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS = [
  "stats",
  "decks",
  "learn",
  "browse",
  "search",
] as const;
export const PRIMARY_NAVIGATION_DESTINATION_OPTIONS = [
  ...DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS,
  "collections",
  "settings",
  "changelog",
  "map",
  "cookbook",
  "idioms",
] as const;
export type PrimaryNavigationDestination = (typeof PRIMARY_NAVIGATION_DESTINATION_OPTIONS)[number];

/**
 * Removed from the strip and the destination toggles. Collections and Idioms temporarily (their
 * routes stay mounted, and admins can still open them from Settings → Admin); Map for good, now
 * that the map is a mode of Learn (`/learn/map`) rather than a tab of its own.
 */
export const HIDDEN_PRIMARY_NAVIGATION_DESTINATIONS = ["collections", "idioms", "map"] as const;

export function isHiddenPrimaryNavigationDestination(
  destination: PrimaryNavigationDestination,
): boolean {
  return (HIDDEN_PRIMARY_NAVIGATION_DESTINATIONS as readonly string[]).includes(destination);
}

export const primaryNavigationDestinationsSettingSchema = z
  .array(z.enum(PRIMARY_NAVIGATION_DESTINATION_OPTIONS))
  .min(1, "Keep at least one primary navigation destination enabled.")
  .max(PRIMARY_NAVIGATION_DESTINATION_OPTIONS.length)
  .refine(
    (tabs) => tabs.every((tab, index) => tabs.indexOf(tab) === index),
    "Tabs must be unique.",
  );

/** Old caches and invalid imported settings must never leave navigation without a primary navigation destination. */
export function resolvePrimaryNavigationDestinations(
  value: unknown,
): readonly PrimaryNavigationDestination[] {
  const result = primaryNavigationDestinationsSettingSchema.safeParse(value);
  return result.success ? result.data : DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS;
}

/**
 * Destinations that may appear in the strip / destination toggles right now. Hidden entries stay
 * valid in stored settings so older exports round-trip, but they never surface until re-enabled.
 */
export function resolveVisiblePrimaryNavigationDestinations(
  value: unknown,
): readonly PrimaryNavigationDestination[] {
  const visible = resolvePrimaryNavigationDestinations(value).filter(
    (destination) => !isHiddenPrimaryNavigationDestination(destination),
  );
  return visible.length > 0 ? visible : DEFAULT_PRIMARY_NAVIGATION_DESTINATIONS;
}
